import { ORDER_STATUS_CREATED } from "../constants/OrderConstant";
import {
  BARCODE_PREFIX,
  BOX_SIZE,
  COMMAND_CODES,
  WAVE_CONTRACT_VERSION,
  WAVE_DEFAULT_SIZE,
  WAVE_STATUS,
} from "../constants/PickingConstant";
import { weightLabel } from "./articleCode";
import { formatBoxAdvice, predictBoxes } from "./boxSize";
import { catalogueForShop, expandOrderToPickLines } from "./pickLines";
import { findRouteSpot } from "./pickRoute";
import { findLinkForProduct, linkCounterpart } from "./productSync";

/**
 * The wave document: the contract between this CMS and the picking app on the freezer scanners.
 *
 * The CMS knows everything and the app knows nothing - no products, variants, packages, shops or
 * Firestore queries. Everything that needs knowledge is decided here, when a round is composed,
 * and frozen into one document: which lines, in which walking order, in which vak, which scans
 * count and how much each one counts, how many boxes. The app shows a line, compares a scanned
 * code with `acceptCodes`, counts down and writes events back. That is why this file is the heart of the system and why
 * `waveDocument.test.js` pins its output against a fixture: the Expo app is built against that
 * fixture (docs/wave-contract-v1.md) and cannot import anything from this repo.
 *
 * Pure: no Firestore, no React.
 */

/** Persistent identity of an order across both shops. `shopSource` itself is never stored. */
export const orderRefOf = (order) => `${order.shopSource}/${order.id}`;

export const splitOrderRef = (orderRef) => {
  const separator = (orderRef || "").indexOf("/");
  if (separator <= 0) return null;
  return { shopSource: orderRef.slice(0, separator), docId: orderRef.slice(separator + 1) };
};

export const waveOrderRefs = (wave) => ((wave && wave.orders) || []).map((order) => order.orderRef);

/**
 * The boxes a round needs, added up from the prediction per order (`boxes[].size`). A whole round
 * is picked in one go, straight into the boxes, so this is what the picker takes into the freezer.
 * An order with products without weight data makes the count an estimate (`uncertainOrders`).
 */
export const summariseWaveBoxes = (wave) => {
  const totals = { small: 0, large: 0, unknown: 0, uncertainOrders: 0 };
  ((wave && wave.orders) || []).forEach((order) => {
    (order.boxes || []).forEach((box) => {
      if (box && box.size === BOX_SIZE.SMALL) totals.small += 1;
      else if (box && box.size === BOX_SIZE.LARGE) totals.large += 1;
      else totals.unknown += 1;
    });
    if (order.linesWithoutWeight > 0) totals.uncertainOrders += 1;
  });
  return totals;
};

/**
 * Orders that may go into a new round, oldest first: past checkout, not gift cards only, and not
 * already in a round. The caller decides how many to take (WAVE_DEFAULT_SIZE by default).
 * Pickup orders are included - the packing slip has always covered them, they come out of the
 * same freezer.
 */
export const selectWaveCandidates = (orders) =>
  (orders || [])
    .filter(
      (order) =>
        !!order &&
        !order.containsOnlyGiftCards &&
        !!order.orderStatus &&
        order.orderStatus !== ORDER_STATUS_CREATED &&
        !(order.picking && order.picking.waveId),
    )
    .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));

export const takeWave = (candidates, size = WAVE_DEFAULT_SIZE) => (candidates || []).slice(0, size);

/**
 * Where a pick line lies: in its own shop's vak, or - for a product that is not on the route
 * itself - in the vak of the product it is linked to in the other shop (productLinks is the one
 * sanctioned "same product" relation). Never by name. `resolvedVia` says which, for the CMS.
 */
export const resolveRouteSpot = (pickRouteIndex, line, links) => {
  const own = findRouteSpot(pickRouteIndex, line.shopSource, line.productId, line.weight);
  if (own) return { spot: own, resolvedVia: "product" };

  const link = findLinkForProduct(links, line.shopSource, line.productId);
  const counterpart = linkCounterpart(link, line.shopSource);
  if (counterpart && counterpart.productId) {
    const linked = findRouteSpot(pickRouteIndex, counterpart.shopSource, counterpart.productId, line.weight);
    if (linked) return { spot: linked, resolvedVia: "link" };
  }
  return { spot: null, resolvedVia: null };
};

const byName = (a, b) => (a.label || "").localeCompare(b.label || "", "nl");

/** Lines with a vak in walking order; lines without one after them, by name. Never dropped. */
const sortWaveLines = (lines) => {
  const placed = lines.filter((line) => !line.offRoute).sort((a, b) => a.pickSequence - b.pickSequence || byName(a, b));
  const unplaced = lines.filter((line) => line.offRoute).sort(byName);
  return [...placed, ...unplaced].map((line, index) => ({ ...line, sequence: index + 1 }));
};

/** Colli barcodes SooCool already has for this order - only once it has been exported. */
const exportedColliBarcodes = (order) =>
  ((order && order.soocool && order.soocool.goods) || []).map((good) => good && good.barcode).filter((barcode) => !!barcode);

/**
 * One order of the wave, or a refusal. Throws nothing: a single bad order must not stop a round.
 */
export const buildWaveOrder = (order, { productIndex, pickRouteIndex, links }) => {
  const orderRef = orderRefOf(order);
  const refuse = (reason, problems) => ({ ok: false, refused: { orderRef, orderId: order.orderId || null, reason, problems } });

  if (!order.orderId) return refuse("no-order-id", ["Bestelling heeft geen ordernummer om op te scannen."]);

  const expansion = expandOrderToPickLines(order, productIndex);
  if (!expansion.ok) return refuse(expansion.reason, expansion.problems);

  let prediction;
  try {
    prediction = predictBoxes(order, catalogueForShop(productIndex, order.shopSource));
  } catch (error) {
    return refuse("box-prediction-failed", [error.message]);
  }

  const lines = sortWaveLines(
    expansion.lines.map((line) => {
      const { spot, resolvedVia } = resolveRouteSpot(pickRouteIndex, line, links);
      return {
        sequence: 0,
        articleCode: line.articleCode,
        shopSource: line.shopSource,
        productId: line.productId,
        label: line.name,
        sub: weightLabel(line.weight),
        quantity: line.quantity,
        // One scan counts a whole package of this many (functions/packaging.js); 1 = per piece
        piecesPerPack: line.piecesPerPack || 1,
        location: spot ? spot.location : null,
        zone: spot ? spot.zone : null,
        acceptCodes: spot && spot.barcode ? [spot.barcode] : [],
        pickNote: line.pickNote,
        pickSequence: spot ? spot.sequence : null,
        offRoute: !spot,
        resolvedVia,
      };
    }),
  );

  const exported = exportedColliBarcodes(order);
  const boxes = prediction.boxes.map((box, index) => ({
    n: box.n,
    size: box.size,
    chargeableGram: box.chargeableGram,
    declaredGram: box.declaredGram,
    contents: box.contents,
    // Only a label that exists can be scanned: before the SooCool export there is none.
    barcode: exported[index] || null,
  }));

  const warnings = [];
  const offRoute = lines.filter((line) => line.offRoute).length;
  if (offRoute > 0) warnings.push({ orderRef, kind: "off-route", count: offRoute });
  if (prediction.productsWithoutWeight.length > 0) {
    warnings.push({ orderRef, kind: "no-weight", count: prediction.productsWithoutWeight.length });
  }
  if (exported.length > 0 && exported.length !== boxes.length) {
    warnings.push({ orderRef, kind: "box-count-mismatch", count: exported.length, predicted: boxes.length });
  }
  const locationless = lines.filter((line) => !line.offRoute && line.acceptCodes.length === 0).length;
  if (locationless > 0) warnings.push({ orderRef, kind: "no-vak-code", count: locationless });
  // A quantity that is not whole packages: the picker takes loose pieces out of a package
  const loose = lines.filter((line) => line.quantity % line.piecesPerPack !== 0).length;
  if (loose > 0) warnings.push({ orderRef, kind: "loose-pieces", count: loose });

  return {
    ok: true,
    warnings,
    waveOrder: {
      sequence: 0,
      orderRef,
      orderId: order.orderId,
      shopSource: order.shopSource,
      orderBarcode: order.orderId,
      acceptCodes: [order.orderId, ...exported],
      deliveryType: order.delivery ? order.delivery.type || null : null,
      deliveryDate: order.delivery ? order.delivery.date || null : null,
      pickupLocation: order.delivery ? order.delivery.pickupLocation || null : null,
      boxAdvice: formatBoxAdvice(boxes),
      boxes,
      linesWithoutWeight: prediction.productsWithoutWeight.length,
      lines,
    },
  };
};

/**
 * Build the wave document for a set of orders. Orders keep the sequence they are handed in
 * (oldest first from selectWaveCandidates); orders that cannot be mapped are refused and listed,
 * the rest form the wave.
 *
 * @returns {{ ok: boolean, wave: Object, refused: Object[], warnings: Object[] }}
 */
export const buildWaveDocument = ({
  waveId,
  waveNumber,
  pickDay,
  gapDays,
  deliveryDates,
  orders,
  productIndex,
  pickRouteIndex,
  links,
  createdBy,
  now,
}) => {
  const refused = [];
  const warnings = [];
  const waveOrders = [];

  (orders || []).forEach((order) => {
    const result = buildWaveOrder(order, { productIndex, pickRouteIndex, links });
    if (!result.ok) {
      refused.push(result.refused);
      return;
    }
    warnings.push(...result.warnings);
    waveOrders.push({ ...result.waveOrder, sequence: waveOrders.length + 1 });
  });

  const wave = {
    contractVersion: WAVE_CONTRACT_VERSION,
    waveId,
    waveNumber,
    pickDay,
    gapDays,
    deliveryDates,
    status: WAVE_STATUS.OPEN,
    createdAt: now,
    createdBy: createdBy || null,
    pickerId: null,
    deviceUid: null,
    startedAt: null,
    finishedAt: null,
    printedAt: null,
    orderCount: waveOrders.length,
    lineCount: waveOrders.reduce((total, waveOrder) => total + waveOrder.lines.length, 0),
    projection: null,
    commands: { ...COMMAND_CODES },
    orders: waveOrders,
  };

  return { ok: waveOrders.length > 0, wave, refused, warnings };
};

/**
 * What must hold for any wave before it is written. The rules that keep a scan unambiguous:
 * order and colli codes are only ever matched exactly and only inside one wave, so no code may
 * belong to two orders, and no vak code may look like an order code or a command.
 */
export const validateWaveDocument = (wave) => {
  const problems = [];
  if (!wave || wave.contractVersion !== WAVE_CONTRACT_VERSION) {
    problems.push(`contractVersion moet ${WAVE_CONTRACT_VERSION} zijn.`);
  }
  const orders = (wave && wave.orders) || [];
  const refs = new Set();
  const orderCodes = new Map();

  orders.forEach((order) => {
    if (refs.has(order.orderRef)) problems.push(`Bestelling ${order.orderRef} staat twee keer in de ronde.`);
    refs.add(order.orderRef);
    (order.acceptCodes || []).forEach((code) => {
      if (orderCodes.has(code) && orderCodes.get(code) !== order.orderRef) {
        problems.push(`Code ${code} hoort bij twee bestellingen in deze ronde.`);
      }
      orderCodes.set(code, order.orderRef);
      if (code.startsWith(BARCODE_PREFIX.LOCATION) || code.startsWith(BARCODE_PREFIX.COMMAND)) {
        problems.push(`Bestelcode ${code} begint met een vak- of commandoprefix.`);
      }
    });
    if (!(order.acceptCodes || []).includes(order.orderBarcode)) {
      problems.push(`Bestelling ${order.orderRef}: de orderbarcode staat niet in acceptCodes.`);
    }
  });

  orders.forEach((order) => {
    (order.lines || []).forEach((line) => {
      if (!Number.isInteger(line.quantity) || line.quantity <= 0) {
        problems.push(`Regel ${line.articleCode} in ${order.orderRef} heeft aantal ${line.quantity}.`);
      }
      if (line.piecesPerPack !== undefined && (!Number.isInteger(line.piecesPerPack) || line.piecesPerPack < 1)) {
        problems.push(`Regel ${line.articleCode} in ${order.orderRef} heeft ${line.piecesPerPack} stuks per verpakking.`);
      }
      (line.acceptCodes || []).forEach((code) => {
        if (!code.startsWith(BARCODE_PREFIX.LOCATION)) {
          problems.push(`Regel ${line.articleCode}: ${code} is geen vakcode.`);
        }
        if (orderCodes.has(code)) problems.push(`Vakcode ${code} is ook een bestelcode.`);
      });
    });
  });

  return { ok: problems.length === 0, problems };
};

/**
 * What each candidate order would become in a round, for the compose screen: mapped with its box
 * advice and warnings, or refused with the reason. The same function the round itself is built
 * with, so the preview cannot promise something the round does not do.
 */
export const previewWaveOrders = ({ orders, productIndex, pickRouteIndex, links }) =>
  (orders || []).map((order) => {
    const result = buildWaveOrder(order, { productIndex, pickRouteIndex, links });
    return {
      order,
      orderRef: orderRefOf(order),
      ok: result.ok,
      waveOrder: result.ok ? result.waveOrder : null,
      warnings: result.ok ? result.warnings : [],
      refused: result.ok ? null : result.refused,
    };
  });
